export type ProjectFile = {
  path: string;
  content?: string;
  language?: string;
  url?: string;
};

export type ProjectReview = {
  hasPreview: boolean;
  hasReadme: boolean;
  hasHosting: boolean;
  hasStartHere: boolean;
  hasConfig: boolean;
  hasPages: boolean;
  sourceFiles: { path: string; language?: string; lines: number }[];
  tips: string[];
};

export type ProjectState = {
  files: Record<string, ProjectFile>;
  preview_path?: string;
  notes?: string;
  notes_log?: {
    at?: string;
    source?: string;
    text?: string;
    message_id?: string;
    files_changed?: string[];
    review?: Partial<ProjectReview>;
  }[];
  imported?: boolean;
  review?: Partial<ProjectReview> & { at?: string; message_id?: string; files_changed?: string[] };
};

export type PreviewPage = {
  id: string;
  label: string;
  kind: 'section' | 'file';
  path?: string;
};

export type PreviewStore = { local: Record<string, string>; session: Record<string, string> };

export type AssemblePreviewOpts = {
  liveChain?: boolean;
  liveFunction?: boolean;
  liveNutrition?: boolean;
  easyAiBase?: string;
  nutritionBase?: string;
  customerId?: string;
  chainIds?: string[];
  previewStore?: PreviewStore;
};

/** Preview sentinel: generated clients treat this as “key present”; Studio proxies auth. */
export const STUDIO_PREVIEW_KEY = 'studio-preview';

export const PREVIEW_STORE_PREFIX = 'easyai-preview-store:';

export function emptyPreviewStore(): PreviewStore {
  return { local: {}, session: {} };
}

export function loadPreviewStore(sessionId: string | null | undefined): PreviewStore {
  if (!sessionId || typeof localStorage === 'undefined') return emptyPreviewStore();
  try {
    const raw = localStorage.getItem(PREVIEW_STORE_PREFIX + sessionId);
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object') return emptyPreviewStore();
    return {
      local: parsed.local && typeof parsed.local === 'object' ? { ...parsed.local } : {},
      session: parsed.session && typeof parsed.session === 'object' ? { ...parsed.session } : {},
    };
  } catch {
    return emptyPreviewStore();
  }
}

export function savePreviewStore(sessionId: string | null | undefined, store: PreviewStore): void {
  if (!sessionId || typeof localStorage === 'undefined') return;
  try {
    const payload = JSON.stringify(store);
    if (payload.length > 1_500_000) return;
    localStorage.setItem(PREVIEW_STORE_PREFIX + sessionId, payload);
  } catch {
    /* quota / private mode */
  }
}

export function clearPreviewStore(sessionId: string | null | undefined): void {
  if (!sessionId || typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(PREVIEW_STORE_PREFIX + sessionId);
  } catch {
    /* ignore */
  }
}

export function applyPreviewStoreOp(
  store: PreviewStore,
  d: { store?: string; op?: string; key?: string; value?: unknown },
): PreviewStore {
  const bucket = d.store === 'session' ? 'session' : 'local';
  const next: PreviewStore = { local: { ...store.local }, session: { ...store.session } };
  if (d.op === 'clear') next[bucket] = {};
  else if (d.op === 'remove' && d.key != null) delete next[bucket][String(d.key)];
  else if (d.op === 'set' && d.key != null) next[bucket][String(d.key)] = String(d.value ?? '');
  return next;
}

function jsonForScript(value: unknown): string {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

export function projectLooksImported(project?: ProjectState | null): boolean {
  if (!project) return false;
  if (project.imported) return true;
  const names = Object.keys(project.files || {});
  if (!names.length) return false;
  const lower = names.map((n) => n.toLowerCase());
  if (lower.some((n) => n === 'package.json' || n.endsWith('/package.json'))) return true;
  const inSrc = names.filter((n) => /(^|\/)src\//.test(n)).length;
  return inSrc >= 5 && names.length >= 8;
}

function normalizePath(p: string): string {
  return (p || '').replace(/^\.\//, '').replace(/^\/+/, '');
}

function fileByHref(files: Record<string, ProjectFile>, href: string): ProjectFile | undefined {
  const path = normalizePath(href.split('?')[0]);
  if (!path || /^https?:\/\//i.test(path) || path.startsWith('data:')) return undefined;
  return files[path];
}

function htmlIsExternalIframeShell(html: string): boolean {
  if (!html.trim()) return false;
  const tags = html.match(/<iframe\b[^>]*>/gi) || [];
  if (!tags.length) return false;
  const external = tags.some((tag) => {
    const m = tag.match(/\bsrc\s*=\s*['"]([^'"]+)['"]/i);
    const src = (m?.[1] || '').trim();
    return /^https?:\/\//i.test(src) || src.startsWith('//');
  });
  if (!external) return false;
  if (/data-page\s*=/i.test(html)) return false;
  const struct = html.match(/<(?:section|main|form|nav|header|article|button|input|textarea|select)\b/gi) || [];
  return struct.length === 0;
}

function previewHtmlFile(project?: ProjectState | null): ProjectFile | undefined {
  const files = project?.files || {};
  const previewPath = project?.preview_path || 'index.html';
  return (
    files[previewPath] ||
    files['index.html'] ||
    files['preview/index.html'] ||
    Object.values(files).find((f) => (f.path || '').toLowerCase().endsWith('.html'))
  );
}

export function extractPreviewPages(project?: ProjectState | null): PreviewPage[] {
  const files = project?.files || {};
  const html = previewHtmlFile(project)?.content || '';
  const found = new Set<string>();
  const re = /data-page=["']([^"']+)["']/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html))) found.add(m[1]);

  const pages: PreviewPage[] = [
    { id: 'all', label: 'All', kind: 'section' },
    { id: 'input', label: 'Input', kind: 'section' },
    { id: 'waiting', label: 'Waiting', kind: 'section' },
    { id: 'results', label: 'Results', kind: 'section' },
  ];
  const seen = new Set(pages.map((p) => p.id));
  found.forEach((id) => {
    if (seen.has(id)) return;
    seen.add(id);
    pages.push({ id, label: id.replace(/[-_]+/g, ' '), kind: 'section' });
  });
  Object.keys(files)
    .filter((p) => p.toLowerCase().endsWith('.html'))
    .sort()
    .forEach((p) => {
      const key = `file:${p}`;
      if (seen.has(key)) return;
      seen.add(key);
      pages.push({ id: key, label: p, kind: 'file', path: p });
    });
  return pages;
}

export function reviewProject(project?: ProjectState | null): ProjectReview {
  const files = project?.files || {};
  const names = Object.keys(files);
  const lower = names.map((n) => n.toLowerCase());
  const html = previewHtmlFile(project)?.content || '';
  const hasPreview = !!html;
  const hasReadme = lower.some((n) => n === 'readme.md' || n.endsWith('/readme.md'));
  const hasHosting = lower.some((n) => n.includes('hosting.md') || n === 'start_here.md');
  const hasStartHere = lower.some((n) => n.endsWith('start_here.md'));
  const hasConfig = names.some(
    (n) => n.toLowerCase().includes('passio.config') || (files[n]?.content || '').includes('PASSIO_CONFIG'),
  );
  const hasPages = /data-page=/i.test(html);
  const sourceFiles = names
    .filter((p) => !p.toLowerCase().endsWith('.html') && !p.toLowerCase().endsWith('.css'))
    .slice(0, 40)
    .map((path) => ({
      path,
      language: files[path]?.language,
      lines: (files[path]?.content || '').split('\n').length,
    }));
  const tips: string[] = [];
  const iframeShell = htmlIsExternalIframeShell(html);
  if (iframeShell) {
    tips.push(
      'Do not make index.html an iframe to an external URL. The real app shell must be in index.html.',
    );
  }
  if (!hasPreview) {
    tips.push('No HTML preview yet. Ask chat to add preview/index.html so you can click through the flow.');
  }
  if (!hasPages && hasPreview && !iframeShell) {
    tips.push('Add data-page="input", data-page="waiting", and data-page="results" so the Page switcher can jump views.');
  }
  if (!hasReadme) tips.push('Add README.md with how to paste the API key and run locally.');
  if (!hasHosting && !hasStartHere) tips.push('Export for hosting to get START_HERE.md and HOSTING.md.');
  if (!hasConfig && hasPreview) tips.push('Ship passio.config.example.js with easyAiBase, easyAiKey, and demoMode.');
  const py = names.filter((n) => n.toLowerCase().endsWith('.py'));
  if (py.length && !hasPreview) {
    tips.push(`Python client is in ${py[0]} — add a sidecar HTML demo so Preview is not blank.`);
  }
  if (!tips.length) tips.push('Looks ready: walk Input → Waiting → Results in Preview, then Export for hosting.');
  return {
    hasPreview,
    hasReadme,
    hasHosting,
    hasStartHere,
    hasConfig,
    hasPages,
    sourceFiles,
    tips,
  };
}

export function applyAssetToHtml(
  html: string,
  selector: string,
  url: string,
  kind: 'image' | 'video',
  fit?: { width?: number; height?: number; role?: string },
): string {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = selector ? doc.querySelector(selector) : null;
    const safeUrl = url.replace(/"/g, '%22');
    const role = (fit?.role || '').toLowerCase();
    const w = fit?.width && fit.width > 8 ? Math.round(fit.width) : 0;
    const h = fit?.height && fit.height > 8 ? Math.round(fit.height) : 0;
    const sizeStyle = () => {
      const parts: string[] = [];
      if (role === 'logo' || role === 'icon') {
        parts.push(`width:${w || 48}px`, `height:${h || 48}px`, 'object-fit:contain', 'display:block');
      } else if (role === 'hero' || role === 'banner') {
        parts.push('width:100%', 'display:block', 'object-fit:cover');
        if (h) parts.push(`height:${h}px`);
      } else {
        if (w) parts.push(`width:${w}px`);
        if (h) parts.push(`height:${h}px`);
        parts.push('object-fit:cover', 'display:block');
      }
      return parts.join(';');
    };
    if (el) {
      const tag = el.tagName.toLowerCase();
      if (kind === 'video' || tag === 'video' || role === 'video') {
        if (tag === 'video') {
          el.setAttribute('src', safeUrl);
          el.setAttribute('autoplay', '');
          el.setAttribute('loop', '');
          el.setAttribute('muted', '');
          el.setAttribute('playsinline', '');
          if (w || h) el.setAttribute('style', `${el.getAttribute('style') || ''};${sizeStyle()}`);
        } else {
          const v = doc.createElement('video');
          v.setAttribute('src', safeUrl);
          v.setAttribute('autoplay', '');
          v.setAttribute('loop', '');
          v.setAttribute('muted', '');
          v.setAttribute('playsinline', '');
          v.setAttribute('style', sizeStyle() || 'width:100%;display:block;object-fit:cover');
          el.replaceWith(v);
        }
      } else if (tag === 'img' || role === 'logo' || role === 'icon' || role === 'image') {
        if (tag === 'img') {
          el.setAttribute('src', safeUrl);
          el.setAttribute('style', `${(el.getAttribute('style') || '').replace(/;?\s*$/, '')};${sizeStyle()}`);
        } else {
          const img = doc.createElement('img');
          img.setAttribute('src', safeUrl);
          img.setAttribute('alt', role || 'placed image');
          img.setAttribute('style', sizeStyle() || 'max-width:100%;display:block');
          el.replaceWith(img);
        }
      } else if (tag === 'button' || /^h[1-6]$/.test(tag)) {
        const prev = (el.getAttribute('style') || '').replace(/;?\s*$/, '');
        el.setAttribute(
          'style',
          `${prev}${prev ? ';' : ''}background-image:url("${safeUrl}");background-size:cover;background-position:center`,
        );
      } else {
        const prev = (el.getAttribute('style') || '').replace(/;?\s*$/, '');
        el.setAttribute(
          'style',
          `${prev}${prev ? ';' : ''}background-image:url("${safeUrl}");background-size:cover;background-position:center${h ? `;min-height:${h}px` : ''}`,
        );
      }
    } else {
      const node = doc.createElement(kind === 'video' ? 'video' : 'img');
      node.setAttribute('src', safeUrl);
      if (kind === 'video') {
        node.setAttribute('autoplay', '');
        node.setAttribute('loop', '');
        node.setAttribute('muted', '');
        node.setAttribute('playsinline', '');
      }
      node.setAttribute('style', sizeStyle() || 'width:100%;display:block');
      (doc.body || doc.documentElement).appendChild(node);
    }
    const dt = doc.doctype ? '<!DOCTYPE html>\n' : '';
    return dt + doc.documentElement.outerHTML;
  } catch {
    return html;
  }
}

const PREVIEW_HEAD = `
<script data-easyai-bridge>
(function () {
  var seed = window.__EASYAI_PREVIEW_STORE || { local: {}, session: {} };
  function memoryStorage(kind) {
    var m = Object.assign({}, seed[kind] || {});
    function sync(op, k, v) {
      try {
        parent.postMessage({ type: "easyai-storage-sync", store: kind, op: op, key: k, value: v }, "*");
      } catch (e0) {}
    }
    var api = {
      getItem: function (k) {
        k = String(k);
        return Object.prototype.hasOwnProperty.call(m, k) ? m[k] : null;
      },
      setItem: function (k, v) {
        m[String(k)] = String(v);
        sync("set", String(k), String(v));
      },
      removeItem: function (k) {
        delete m[String(k)];
        sync("remove", String(k));
      },
      clear: function () {
        m = {};
        sync("clear");
      },
      key: function (i) {
        return Object.keys(m)[i] || null;
      },
    };
    return new Proxy(api, {
      get: function (t, p) {
        if (p === "length") return Object.keys(m).length;
        if (p in t) return t[p];
        if (typeof p === "string" && Object.prototype.hasOwnProperty.call(m, p)) return m[p];
        return undefined;
      },
      set: function (t, p, v) {
        if (p === "length") return true;
        if (typeof t[p] === "function") return true;
        m[String(p)] = String(v);
        sync("set", String(p), String(v));
        return true;
      },
      deleteProperty: function (t, p) {
        delete m[String(p)];
        sync("remove", String(p));
        return true;
      },
    });
  }
  function installStorage(name) {
    var store = memoryStorage(name === "sessionStorage" ? "session" : "local");
    try {
      delete window[name];
    } catch (e0) {}
    try {
      Object.defineProperty(window, name, {
        configurable: true,
        enumerable: true,
        writable: true,
        value: store,
      });
    } catch (e2) {
      try {
        window[name] = store;
      } catch (e3) {}
    }
  }
  installStorage("localStorage");
  installStorage("sessionStorage");

  window.addEventListener("error", function (ev) {
    var msg = (ev && ev.message) || "Script error in preview";
    try {
      parent.postMessage({ type: "easyai-preview-error", message: msg }, "*");
    } catch (e) {}
  });
  window.addEventListener("unhandledrejection", function (ev) {
    var r = ev && ev.reason;
    var msg = r && r.message ? r.message : String(r || "Unhandled preview error");
    try {
      parent.postMessage({ type: "easyai-preview-error", message: msg }, "*");
    } catch (e) {}
  });
  try {
    parent.postMessage({ type: "easyai-preview-ready" }, "*");
  } catch (eReady) {}

  document.addEventListener(
    "submit",
    function (e) {
      e.preventDefault();
    },
    true
  );
  if (window.HTMLFormElement && HTMLFormElement.prototype.submit) {
    HTMLFormElement.prototype.submit = function () {
      this.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
    };
  }

  function proxyResult(type, payload) {
    return new Promise(function (resolve, reject) {
      var id = "p" + Math.random().toString(36).slice(2);
      var done = false;
      var timer = setTimeout(function () {
        if (done) return;
        done = true;
        window.removeEventListener("message", onMsg);
        reject(new Error("Timed out waiting for Studio to run this request"));
      }, 180000);
      function onMsg(ev) {
        var d = ev.data;
        if (!d || d.type !== type || d.id !== id) return;
        if (done) return;
        done = true;
        clearTimeout(timer);
        window.removeEventListener("message", onMsg);
        var hdrs = { "Content-Type": "application/json" };
        if (d.requestId) hdrs["X-Request-ID"] = d.requestId;
        if (d.runId) hdrs["EASYAI-RUN-ID"] = d.runId;
        if (d.error && d.status && d.status >= 400) {
          resolve(
            new Response(JSON.stringify(d.body || { detail: d.error }), {
              status: d.status,
              headers: hdrs,
            })
          );
          return;
        }
        if (d.error && !d.body) {
          reject(new Error(d.error));
          return;
        }
        resolve(
          new Response(JSON.stringify(d.body || {}), {
            status: d.status || 200,
            headers: hdrs,
          })
        );
      }
      window.addEventListener("message", onMsg);
      parent.postMessage(Object.assign({ id: id }, payload), "*");
    });
  }

  function proxyForUrl(url, method, body) {
    var u = String(url || "");
    var exec = u.match(/\\/api\\/v2\\/chains\\/([^/?#]+)\\/execute\\/?/);
    if (!exec) exec = u.match(/\\/chains\\/([0-9a-fA-F-]{36})\\/execute\\/?/);
    var poll = u.match(/\\/api\\/v2\\/chains\\/executions\\/([^/?#]+)/);
    if (!poll) poll = u.match(/\\/chains\\/executions\\/([^/?#]+)/);
    var fb = u.match(/\\/api\\/v2\\/feedback(\\/batch)?\\/?(?:\\?|$)/);
    if (fb && String(method || "GET").toUpperCase() === "POST") {
      return proxyResult("easyai-feedback-proxy-result", {
        type: "easyai-feedback-proxy",
        batch: !!fb[1],
        body: body,
      });
    }
    var fn = u.match(/\\/api\\/v2\\/functions\\/([^/?#]+)\\/([^/?#]+)(?:\\/)?(?:\\?|$)/);
    if (fn && fn[2] !== "route-status" && u.indexOf("/form") < 0) {
      return proxyResult("easyai-fn-proxy-result", {
        type: "easyai-fn-proxy",
        customerId: fn[1],
        name: fn[2],
        method: method || "POST",
        body: body,
      });
    }
    var nutrPath = null;
    var nutrHost = /passiolife\\.com/i.test(u) || /api-dev\\.passiolife/i.test(u);
    var tokenHit = u.match(/\\/v2\\/token-cache\\/unified\\/oauth\\/token\\/([^/?#]+)/);
    var productsHit = u.match(/(\\/v2\\/products\\/(?:napi\\/|food\\/|tools\\/)[^?#]*)/);
    if (tokenHit) nutrPath = "/v2/token-cache/unified/oauth/token/" + decodeURIComponent(tokenHit[1]);
    else if (productsHit) nutrPath = productsHit[1];
    if (nutrPath && (nutrHost || nutrPath.indexOf("/v2/products/") === 0 || nutrPath.indexOf("/v2/token-cache/") === 0)) {
      var q = {};
      try {
        var qi = u.indexOf("?");
        if (qi >= 0) {
          String(u.slice(qi + 1)).split("&").forEach(function (pair) {
            var kv = pair.split("=");
            if (kv[0]) q[decodeURIComponent(kv[0])] = decodeURIComponent((kv[1] || "").replace(/\\+/g, " "));
          });
        }
      } catch (e) {}
      var parsedBody = null;
      if (body) {
        try { parsedBody = JSON.parse(body); } catch (e2) { parsedBody = body; }
      }
      return proxyResult("easyai-nutrition-proxy-result", {
        type: "easyai-nutrition-proxy",
        method: method || "GET",
        path: nutrPath.split("?")[0],
        query: q,
        body: parsedBody,
      });
    }
    if (exec || poll) {
      try {
        window.__easyaiChainProxyAt = Date.now();
      } catch (e) {}
      return proxyResult("easyai-chain-proxy-result", {
        type: "easyai-chain-proxy",
        action: exec ? "execute" : "poll",
        chainId: exec ? exec[1] : undefined,
        executionId: poll ? poll[1] : undefined,
        method: method || (exec ? "POST" : "GET"),
        body: body,
      });
    }
    return null;
  }

  function readBody(body) {
    if (body == null) return body;
    if (typeof body === "string") return body;
    if (typeof FormData !== "undefined" && body instanceof FormData) {
      var o = {};
      body.forEach(function (v, k) {
        o[k] = v;
      });
      return JSON.stringify({ input_data: o });
    }
    try {
      return JSON.stringify(body);
    } catch (e) {
      return String(body);
    }
  }

  var origFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === "string" ? input : (input && input.url) || "";
    var method = (init && init.method) || (input && input.method) || "GET";
    var body = readBody(init && init.body);
    var proxied = proxyForUrl(url, method, body);
    if (proxied) return proxied;
    return origFetch.apply(this, arguments);
  };
  var OrigXHR = window.XMLHttpRequest;
  if (OrigXHR && OrigXHR.prototype) {
    var origOpen = OrigXHR.prototype.open;
    var origSend = OrigXHR.prototype.send;
    OrigXHR.prototype.open = function (method, url) {
      this.__easyaiUrl = url;
      this.__easyaiMethod = method;
      return origOpen.apply(this, arguments);
    };
    OrigXHR.prototype.send = function (body) {
      var proxied = proxyForUrl(this.__easyaiUrl, this.__easyaiMethod, readBody(body));
      if (!proxied) return origSend.apply(this, arguments);
      var self = this;
      proxied
        .then(function (res) {
          return res.text().then(function (text) {
            Object.defineProperty(self, "status", { configurable: true, value: res.status });
            Object.defineProperty(self, "readyState", { configurable: true, value: 4 });
            Object.defineProperty(self, "responseText", { configurable: true, value: text });
            Object.defineProperty(self, "response", { configurable: true, value: text });
            if (typeof self.onload === "function") self.onload();
            self.dispatchEvent(new Event("load"));
            self.dispatchEvent(new Event("loadend"));
          });
        })
        .catch(function (err) {
          if (typeof self.onerror === "function") self.onerror(err);
          self.dispatchEvent(new Event("error"));
        });
    };
  }
})();
</script>
`;

const PREVIEW_BOOT = `
<script data-easyai-boot>
(function () {
  function pagesUseVisibleClass(pages) {
    return Array.prototype.some.call(pages, function (el) {
      return el.classList.contains("is-visible") || el.classList.contains("page");
    });
  }
  function showPage(name) {
    var pages = document.querySelectorAll("[data-page]");
    if (!pages.length) return;
    var usesClass = pagesUseVisibleClass(pages);
    if (name === "all") {
      pages.forEach(function (el) {
        el.classList.add("is-visible");
        el.removeAttribute("hidden");
        el.style.removeProperty("display");
      });
      return;
    }
    pages.forEach(function (el) {
      var on = el.getAttribute("data-page") === name;
      el.classList.toggle("is-visible", on);
      el.style.removeProperty("display");
      if (usesClass) el.removeAttribute("hidden");
      else el.hidden = !on;
    });
  }
  window.__easyaiShowPage = showPage;

  function cssEsc(v) {
    v = String(v || "");
    try {
      if (window.CSS && CSS.escape) return CSS.escape(v);
    } catch (eEsc) {}
    return v.replace(/[^a-zA-Z0-9_-]/g, "\\\\$&");
  }
  function cssPath(el) {
    if (!el || el.nodeType !== 1) return "";
    if (el.id) return "#" + cssEsc(el.id);
    var attr = el.getAttribute("data-page") || el.getAttribute("data-easyai") || el.getAttribute("aria-label");
    if (attr && el.getAttribute("data-page")) return "[data-page='" + attr.replace(/'/g, "") + "']";
    var parts = [];
    while (el && el.nodeType === 1 && parts.length < 6) {
      var s = el.tagName.toLowerCase();
      if (el.id) {
        parts.unshift("#" + cssEsc(el.id));
        break;
      }
      if (el.className && typeof el.className === "string") {
        var cls = el.className.trim().split(/\\s+/).filter(function (c) {
          return c && c.indexOf(":") === -1 && c.indexOf("is-") !== 0;
        }).slice(0, 2);
        if (cls.length) s += "." + cls.join(".");
      }
      var parent = el.parentElement;
      if (parent) {
        var kids = Array.prototype.filter.call(parent.children, function (c) {
          return c.tagName === el.tagName;
        });
        if (kids.length > 1) {
          var idx = Array.prototype.indexOf.call(kids, el) + 1;
          s += ":nth-of-type(" + idx + ")";
        }
      }
      parts.unshift(s);
      if (!parent || parent.tagName === "HTML") break;
      el = parent;
    }
    return parts.join(" > ");
  }

  function describeEl(el) {
    if (!el || !el.getBoundingClientRect) return null;
    var r = el.getBoundingClientRect();
    var cs = window.getComputedStyle(el);
    var tag = (el.tagName || "").toLowerCase();
    var text = (el.innerText || el.getAttribute("alt") || el.getAttribute("aria-label") || "").replace(/\\s+/g, " ").trim().slice(0, 80);
    var w = Math.round(r.width);
    var h = Math.round(r.height);
    var aspect = h ? Math.round((w / h) * 100) / 100 : 0;
    var role = "block";
    if (tag === "video") role = "video";
    else if (tag === "img" || tag === "svg") {
      if (w <= 72 && h <= 72) role = "icon";
      else if (w <= 180 && h <= 80) role = "logo";
      else if (w >= 480 || (w >= 280 && h >= 160)) role = "hero";
      else role = "image";
    } else if (tag === "button" || el.getAttribute("role") === "button") role = "button";
    else if (/^h[1-6]$/.test(tag)) role = "heading";
    else if (w >= 480 && h >= 140) role = "hero";
    else if ((cs.backgroundImage || "none") !== "none") role = "background";
    return {
      selector: cssPath(el),
      tag: tag,
      id: el.id || "",
      className: typeof el.className === "string" ? el.className.slice(0, 80) : "",
      text: text,
      role: role,
      rect: { x: Math.round(r.left), y: Math.round(r.top), w: w, h: h, aspect: aspect },
      styles: {
        background: (cs.backgroundColor || "").slice(0, 40),
        color: (cs.color || "").slice(0, 40),
        font: ((cs.fontFamily || "") + " " + (cs.fontSize || "")).slice(0, 80),
        radius: cs.borderRadius || "",
      },
    };
  }

  function pageBackground() {
    try {
      return window.getComputedStyle(document.body).backgroundColor || "#ffffff";
    } catch (err) {
      return "#ffffff";
    }
  }

  function regionShot(box, hits) {
    try {
      var canvas = document.createElement("canvas");
      var max = 720;
      var scale = Math.min(1, max / Math.max(box.w || 1, box.h || 1, 1));
      canvas.width = Math.max(8, Math.round(box.w * scale));
      canvas.height = Math.max(8, Math.round(box.h * scale));
      var ctx = canvas.getContext("2d");
      ctx.fillStyle = pageBackground();
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      Array.prototype.forEach.call(document.images || [], function (im) {
        if (!im.complete || !im.naturalWidth) return;
        var r = im.getBoundingClientRect();
        var ox = Math.max(r.left, box.x);
        var oy = Math.max(r.top, box.y);
        var ox2 = Math.min(r.right, box.x + box.w);
        var oy2 = Math.min(r.bottom, box.y + box.h);
        if (ox2 - ox < 4 || oy2 - oy < 4) return;
        try {
          var sx = (ox - r.left) * (im.naturalWidth / Math.max(r.width, 1));
          var sy = (oy - r.top) * (im.naturalHeight / Math.max(r.height, 1));
          var sw = (ox2 - ox) * (im.naturalWidth / Math.max(r.width, 1));
          var sh = (oy2 - oy) * (im.naturalHeight / Math.max(r.height, 1));
          ctx.drawImage(
            im,
            sx, sy, sw, sh,
            (ox - box.x) * scale,
            (oy - box.y) * scale,
            (ox2 - ox) * scale,
            (oy2 - oy) * scale
          );
        } catch (eImg) {}
      });
      (hits || []).slice(0, 8).forEach(function (hit, i) {
        var rr = hit.rect || {};
        var st = hit.styles || {};
        var x = ((rr.x || 0) - box.x) * scale;
        var y = ((rr.y || 0) - box.y) * scale;
        var ww = (rr.w || 0) * scale;
        var hh = (rr.h || 0) * scale;
        if (ww < 2 || hh < 2) return;
        var fill = st.background || "";
        if (fill && fill !== "rgba(0, 0, 0, 0)" && fill !== "transparent") {
          ctx.fillStyle = fill;
          ctx.globalAlpha = 0.55;
          ctx.fillRect(x, y, ww, hh);
          ctx.globalAlpha = 1;
        }
        ctx.strokeStyle = i === 0 ? "#38bdf8" : "rgba(15,23,42,0.35)";
        ctx.lineWidth = i === 0 ? 2 : 1;
        ctx.strokeRect(x + 0.5, y + 0.5, Math.max(0, ww - 1), Math.max(0, hh - 1));
        var role = String(hit.role || hit.tag || "area").toLowerCase();
        if (role === "image" || role === "logo" || role === "icon" || role === "hero" || role === "video") return;
        ctx.fillStyle = st.color || "#0f172a";
        ctx.font = Math.max(10, Math.min(14, hh * 0.45)) + "px sans-serif";
        var text = String(hit.text || "").replace(/\\s+/g, " ").trim();
        if (!text) {
          text = ({ block: "Area", background: "Section", hero: "Hero", banner: "Banner", button: "Button", heading: "Heading", logo: "Logo", icon: "Icon", image: "Image", video: "Video", input: "Input" })[role] || role;
        }
        if (ww > 20 && hh > 10) {
          while (text.length > 2 && ctx.measureText(text).width > ww - 8) text = text.slice(0, -1);
          ctx.fillText(text, x + 4, y + Math.min(hh - 4, Math.max(12, hh * 0.55)));
        }
      });
      ctx.strokeStyle = "#38bdf8";
      ctx.lineWidth = 2;
      ctx.strokeRect(1, 1, canvas.width - 2, canvas.height - 2);
      return canvas.toDataURL("image/png");
    } catch (err) {
      return null;
    }
  }

  function hitsForBox(box) {
    var hits = [];
    var all = document.body ? document.body.querySelectorAll("*") : [];
    Array.prototype.forEach.call(all, function (el) {
      if (el.getAttribute && el.getAttribute("data-easyai-marquee")) return;
      var r = el.getBoundingClientRect();
      if (r.width < 4 || r.height < 4) return;
      var ox = Math.max(0, Math.min(r.right, box.x + box.w) - Math.max(r.left, box.x));
      var oy = Math.max(0, Math.min(r.bottom, box.y + box.h) - Math.max(r.top, box.y));
      if (ox * oy < 40) return;
      var info = describeEl(el);
      if (info) hits.push(info);
    });
    hits.sort(function (a, b) { return a.rect.w * a.rect.h - b.rect.w * b.rect.h; });
    if (hits.length > 1) {
      hits = hits.filter(function (h) {
        var tag = h.tag || "";
        if (tag === "body" || tag === "html") return false;
        var area = (h.rect.w || 0) * (h.rect.h || 0);
        if (h.role === "block" && area > box.w * box.h * 0.88) return false;
        return true;
      });
    }
    var uniq = [];
    hits.forEach(function (h) {
      var dup = uniq.some(function (u) {
        return u.role === h.role && (u.text || "") === (h.text || "") && Math.abs((u.rect.w || 0) - (h.rect.w || 0)) < 12;
      });
      if (!dup) uniq.push(h);
    });
    return uniq.slice(0, 6);
  }

  function emitRegion(box) {
    var hits = hitsForBox(box);
    parent.postMessage(
      {
        type: "easyai-region",
        box: box,
        hits: hits,
        image: regionShot(box, hits),
      },
      "*"
    );
  }

  var pickOn = false;
  var regionOn = false;
  var hoverEl = null;
  var drag = null;
  var marquee = null;
  function ensureMarquee() {
    if (marquee) return marquee;
    marquee = document.createElement("div");
    marquee.setAttribute("data-easyai-marquee", "1");
    marquee.style.cssText = "position:fixed;z-index:2147483646;border:2px dashed #38bdf8;background:rgba(56,189,248,0.12);pointer-events:none;display:none";
    (document.body || document.documentElement).appendChild(marquee);
    return marquee;
  }
  function setHover(el) {
    if (hoverEl && hoverEl !== el) hoverEl.style.outline = hoverEl.getAttribute("data-easyai-old-outline") || "";
    hoverEl = el;
    if (!el) return;
    if (!el.getAttribute("data-easyai-old-outline")) el.setAttribute("data-easyai-old-outline", el.style.outline || "");
    el.style.outline = "2px solid #38bdf8";
  }
  document.addEventListener(
    "mouseover",
    function (e) {
      if (!pickOn) return;
      var t = e.target;
      if (t && t.tagName) setHover(t);
    },
    true
  );
  document.addEventListener(
    "click",
    function (e) {
      if (!pickOn) return;
      e.preventDefault();
      e.stopPropagation();
      var t = e.target;
      if (!t || !t.tagName) return;
      var info = describeEl(t);
      parent.postMessage(Object.assign({ type: "easyai-pick" }, info || { selector: cssPath(t), tag: t.tagName }), "*");
    },
    true
  );
  document.addEventListener(
    "mousedown",
    function (e) {
      if (!regionOn) return;
      e.preventDefault();
      e.stopPropagation();
      drag = { x: e.clientX, y: e.clientY };
      var m = ensureMarquee();
      m.style.display = "block";
      m.style.left = drag.x + "px";
      m.style.top = drag.y + "px";
      m.style.width = "0px";
      m.style.height = "0px";
    },
    true
  );
  document.addEventListener(
    "mousemove",
    function (e) {
      if (!regionOn || !drag) return;
      var x = Math.min(e.clientX, drag.x);
      var y = Math.min(e.clientY, drag.y);
      var w = Math.abs(e.clientX - drag.x);
      var h = Math.abs(e.clientY - drag.y);
      var m = ensureMarquee();
      m.style.left = x + "px";
      m.style.top = y + "px";
      m.style.width = w + "px";
      m.style.height = h + "px";
    },
    true
  );
  document.addEventListener(
    "mouseup",
    function (e) {
      if (!regionOn || !drag) return;
      e.preventDefault();
      e.stopPropagation();
      var box = {
        x: Math.min(e.clientX, drag.x),
        y: Math.min(e.clientY, drag.y),
        w: Math.max(8, Math.abs(e.clientX - drag.x)),
        h: Math.max(8, Math.abs(e.clientY - drag.y)),
      };
      drag = null;
      if (marquee) marquee.style.display = "none";
      emitRegion(box);
    },
    true
  );

  window.addEventListener("message", function (e) {
    var d = e.data;
    if (!d) return;
    if (d.type === "easyai-show-page" && d.page) showPage(d.page);
    if (d.type === "easyai-set-pick") {
      pickOn = !!d.on;
      regionOn = false;
      document.documentElement.style.cursor = pickOn ? "crosshair" : "";
      if (!pickOn) setHover(null);
    }
    if (d.type === "easyai-set-region") {
      regionOn = !!d.on;
      pickOn = false;
      document.documentElement.style.cursor = regionOn ? "crosshair" : "";
      setHover(null);
    }
    if (d.type === "easyai-region-query" && d.box) {
      try {
        emitRegion(d.box);
      } catch (eQuery) {}
    }
    if (d.type === "easyai-run-now") {
      if (!(cfg().chainId || (cfg().chainIds && cfg().chainIds[0]))) return;
      var nowInput = d.input || collectInput();
      var nowHas = Object.keys(nowInput).some(function (k) {
        var v = nowInput[k];
        return typeof v === "string" ? v.trim() : v != null && v !== false;
      });
      if (!nowHas) {
        try {
          parent.postMessage({ type: "easyai-preview-error", message: "Type something in the form first, then submit." }, "*");
        } catch (eNow) {}
        return;
      }
      runAttachedChain(nowInput);
    }
  });

  window.onerror = function (msg, _src, line) {
    try {
      parent.postMessage(
        { type: "easyai-preview-error", message: String(msg) + (line ? " (line " + line + ")" : "") },
        "*"
      );
    } catch (eErr) {}
  };
  window.addEventListener("unhandledrejection", function (ev) {
    try {
      var r = ev && ev.reason;
      parent.postMessage({ type: "easyai-preview-error", message: String((r && r.message) || r || "Unhandled error") }, "*");
    } catch (eRej) {}
  });

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function collectInput() {
    var data = {};
    var root = document.querySelector("[data-page='input']") || document.body || document;
    var fields = root.querySelectorAll("input, textarea, select");
    Array.prototype.forEach.call(fields, function (el) {
      if (el.type === "button" || el.type === "submit" || el.type === "reset") return;
      var k = el.name || el.id || el.getAttribute("aria-label") || el.getAttribute("placeholder") || "";
      k = String(k).replace(/\\s+/g, "_").slice(0, 48);
      if (el.type === "checkbox") {
        if (k) data[k] = el.checked;
        return;
      }
      if (el.type === "radio") {
        if (k && el.checked) data[k] = el.value;
        return;
      }
      var val = el.value;
      if (k) data[k] = val;
      else if (typeof val === "string" && val.trim() && !data.__unnamed) data.__unnamed = val.trim();
    });
    var texts = [];
    Object.keys(data).forEach(function (k) {
      if (typeof data[k] === "string" && data[k].trim()) texts.push(data[k].trim());
    });
    if (data.__unnamed && texts.indexOf(data.__unnamed) < 0) texts.push(data.__unnamed);
    delete data.__unnamed;
    if (texts.length >= 1) {
      var v = texts[0];
      if (!data.grocery_input) data.grocery_input = v;
      if (!data.item) data.item = v;
      if (!data.query) data.query = v;
      if (!data.text) data.text = v;
      if (!data.items) data.items = texts.length === 1 ? [v] : texts.slice();
    }
    return data;
  }

  function cfg() {
    return window.PASSIO_CONFIG || {};
  }

  function pickSink(row) {
    var nr = row && row.node_results;
    if (!nr || typeof nr !== "object") return row && row.result ? row.result : row;
    var keys = Object.keys(nr);
    var last = nr[keys[keys.length - 1]];
    if (last && typeof last === "object") return last.output || last.result || last.data || last;
    return row;
  }

  function fillWaiting(text) {
    showPage("waiting");
    var el = document.querySelector("[data-page='waiting']");
    if (!el) return;
    if (el.querySelector("#statusMessage, .loading-panel, [data-easyai-progress]")) return;
    var box = el.querySelector("[data-easyai-wait]");
    if (!box) {
      box = document.createElement("div");
      box.setAttribute("data-easyai-wait", "1");
      box.style.cssText = "padding:20px;font-family:system-ui,sans-serif;text-align:center";
      el.insertBefore(box, el.firstChild);
    }
    box.innerHTML = "<p style='margin:0;font-weight:700'>Running your chain…</p><p style='color:#64748b;margin:8px 0 0'>" + esc(text || "") + "</p>";
  }

  function fillResults(row) {
    showPage("results");
    var el = document.querySelector("[data-page='results']");
    if (!el) {
      el = document.createElement("section");
      el.setAttribute("data-page", "results");
      (document.body || document.documentElement).appendChild(el);
    }
    var sink = pickSink(row) || {};
    var items = sink.items || sink.replacements || [];
    var html = "";
    if (items && items.length) {
      html = items
        .map(function (it) {
          var reps = it.suggested_replacements || it.replacements || [];
          var cards = (reps || [])
            .slice(0, 10)
            .map(function (r) {
              return (
                "<div style='border:1px solid #e5e7eb;border-radius:12px;padding:12px;margin:8px 0'>" +
                "<strong>" +
                esc(r.name || r.title || "Replacement") +
                "</strong> " +
                esc(r.brand || "") +
                (r.score != null ? "<div style='font-size:12px;color:#64748b'>score " + esc(r.score) + "</div>" : "") +
                "<p style='margin:8px 0 0;font-size:13px'>" +
                esc(r.reason || r.why || "") +
                "</p></div>"
              );
            })
            .join("");
          return (
            "<div style='margin-bottom:20px'><h3 style='margin:0 0 8px'>" +
            esc(it.original_item || it.original || it.name || "Item") +
            "</h3>" +
            (it.original_already_good ? "<p style='color:#15803d'>Already a good fit.</p>" : "") +
            (cards || "<p>No replacements returned.</p>") +
            "</div>"
          );
        })
        .join("");
    } else if (row && row.error) {
      html = "<p style='color:#b91c1c'>" + esc(row.error) + "</p>";
    } else {
      html = "<pre style='white-space:pre-wrap;font-size:12px;background:#f8fafc;padding:12px;border-radius:8px'>" + esc(JSON.stringify(sink, null, 2)) + "</pre>";
    }
    var host = el.querySelector("[data-easyai-results]") || document.createElement("div");
    host.setAttribute("data-easyai-results", "1");
    host.style.cssText = "padding:16px;font-family:system-ui,sans-serif";
    host.innerHTML = html;
    if (!host.parentNode) el.appendChild(host);
  }

  var chainRunLock = false;
  async function runAttachedChain(inputData) {
    if (chainRunLock) return;
    chainRunLock = true;
    var id = cfg().chainId || (cfg().chainIds && cfg().chainIds[0]) || "attached";
    var base = cfg().easyAiBase || "";
    fillWaiting("Starting…");
    window.__easyaiChainProxyAt = Date.now();
    try {
      var start = await fetch(base + "/api/v2/chains/" + id + "/execute", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ input_data: inputData || {} }),
      });
      var exec = await start.json();
      if (!start.ok) {
        var startErr = exec.detail || exec.error || "Execute failed";
        throw new Error(typeof startErr === "string" ? startErr : JSON.stringify(startErr));
      }
      var eid = exec.id;
      var row = exec;
      for (var i = 0; i < 120; i++) {
        fillWaiting(row.status || "running");
        if (row.status === "completed" || row.status === "failed" || row.status === "cancelled") break;
        await new Promise(function (r) {
          setTimeout(r, 1500);
        });
        var poll = await fetch(base + "/api/v2/chains/executions/" + eid, { headers: { "Content-Type": "application/json" } });
        row = await poll.json();
        if (!poll.ok) {
          var pollErr = row.detail || row.error || "Poll failed";
          throw new Error(typeof pollErr === "string" ? pollErr : JSON.stringify(pollErr));
        }
      }
      if (row.status === "failed" || row.status === "cancelled") {
        fillResults(row);
        try {
          parent.postMessage({ type: "easyai-preview-error", message: row.error || row.status }, "*");
        } catch (e1) {}
        return row;
      }
      fillResults(row);
      try {
        window.dispatchEvent(new CustomEvent("easyai-chain-result", { detail: row }));
      } catch (eEvt) {}
      return row;
    } catch (err) {
      fillWaiting("Error");
      try {
        parent.postMessage({ type: "easyai-preview-error", message: err && err.message ? err.message : String(err) }, "*");
      } catch (e3) {}
      throw err;
    } finally {
      chainRunLock = false;
    }
  }
  window.__easyaiRunChain = runAttachedChain;

  function scheduleStudioRun() {
    setTimeout(function () {
      var id = cfg().chainId || (cfg().chainIds && cfg().chainIds[0]) || "";
      if (!id || /YOUR_|placeholder|attached/i.test(String(id))) return;
      if (!/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(String(id))) return;
      if (Date.now() - (window.__easyaiChainProxyAt || 0) < 2500) return;
      var input = collectInput();
      var has = Object.keys(input).some(function (k) {
        var v = input[k];
        return typeof v === "string" ? v.trim() : v != null && v !== false;
      });
      if (!has) {
        try {
          parent.postMessage({ type: "easyai-preview-error", message: "Type something in the form first, then submit." }, "*");
        } catch (e) {}
        return;
      }
      runAttachedChain(input);
    }, 800);
  }

  document.addEventListener(
    "submit",
    function () {
      scheduleStudioRun();
    },
    false
  );
  document.addEventListener(
    "click",
    function (e) {
      if (pickOn) return;
      if (!e.target || typeof e.target.closest !== "function") return;
      var btn = e.target.closest("button, input[type=submit]");
      if (!btn) return;
      var label = ((btn.textContent || btn.value || "") + " " + (btn.getAttribute("aria-label") || "")).toLowerCase();
      var explicitSubmit =
        (btn.getAttribute("type") || "").toLowerCase() === "submit" ||
        (btn.tagName === "INPUT" && String(btn.type).toLowerCase() === "submit");
      var runLike =
        explicitSubmit ||
        /submit|run|search|replace|find|go|start|analyze|check|generate|swap|look up/.test(label);
      if (!runLike) return;
      scheduleStudioRun();
    },
    true
  );
})();
</script>
`;

function injectBoot(html: string, opts?: AssemblePreviewOpts): string {
  const live = !!(opts?.liveChain || opts?.liveFunction || opts?.liveNutrition);
  const base = JSON.stringify(opts?.easyAiBase || '');
  const nutr = JSON.stringify(opts?.nutritionBase || '');
  const cust = JSON.stringify(opts?.customerId || '');
  const chainId = (opts?.chainIds || []).filter(Boolean)[0] || '';
  const chainIds = JSON.stringify((opts?.chainIds || []).filter(Boolean));
  const extraBits = [
    `demoMode: false`,
    `easyAiKey: ${JSON.stringify(STUDIO_PREVIEW_KEY)}`,
  ];
  if (opts?.easyAiBase) extraBits.push(`easyAiBase: ${base}`);
  if (opts?.customerId) extraBits.push(`customerId: ${cust}`);
  if (opts?.liveChain && chainId) {
    extraBits.push(`chainId: ${JSON.stringify(chainId)}`, `chainIds: ${chainIds}`);
  } else {
    extraBits.push(`chainId: ""`, `chainIds: []`);
  }
  if (opts?.liveNutrition) {
    extraBits.push(`nutritionKey: ${JSON.stringify(STUDIO_PREVIEW_KEY)}`);
    if (opts.nutritionBase) extraBits.push(`nutritionBase: ${nutr}`);
  }
  const store = opts?.previewStore || emptyPreviewStore();
  const storeHead = `<script data-easyai-store>window.__EASYAI_PREVIEW_STORE=${jsonForScript(store)};</script>`;
  const head = storeHead + PREVIEW_HEAD;
  const liveHead = live
    ? `<script data-easyai-live>
(function(){
  var extras = { ${extraBits.join(', ')} };
  var cfg = Object.assign({}, window.PASSIO_CONFIG || {}, extras);
  Object.defineProperty(window, "PASSIO_CONFIG", {
    configurable: true,
    get: function () { return cfg; },
    set: function (v) { cfg = Object.assign({}, v || {}, extras); }
  });
})();
</script>`
    : '';
  if (liveHead) {
    if (/<head[^>]*>/i.test(html)) html = html.replace(/<head[^>]*>/i, (m) => `${m}${head}${liveHead}`);
    else if (/<html[^>]*>/i.test(html)) html = html.replace(/<html[^>]*>/i, (m) => `${m}${head}${liveHead}`);
    else html = head + liveHead + html;
  } else if (/<head[^>]*>/i.test(html)) {
    html = html.replace(/<head[^>]*>/i, (m) => `${m}${head}`);
  } else if (/<html[^>]*>/i.test(html)) {
    html = html.replace(/<html[^>]*>/i, (m) => `${m}${head}`);
  } else {
    html = head + html;
  }
  if (/<\/body>/i.test(html)) return html.replace(/<\/body>/i, `${PREVIEW_BOOT}</body>`);
  return html + PREVIEW_BOOT;
}

function sourceReviewHtml(project: ProjectState): string {
  const review = reviewProject(project);
  const files = Object.keys(project.files || {})
    .sort()
    .map((p) => {
      const rec = project.files[p];
      const n = rec.content ? rec.content.split('\n').length : rec.url ? 'asset' : 0;
      return `<li><code>${p}</code> <span>${n}${typeof n === 'number' ? ' lines' : ''}</span></li>`;
    })
    .join('');
  const readme =
    Object.values(project.files || {}).find((f) => (f.path || '').toLowerCase().endsWith('readme.md'))?.content ||
    '';
  const tips = review.tips.map((t) => `<li>${t}</li>`).join('');
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
    body{font-family:ui-sans-serif,system-ui,sans-serif;margin:0;padding:24px;background:#0b1220;color:#e2e8f0}
    h1{font-size:1.15rem;margin:0 0 8px} p,.muted{color:#94a3b8;font-size:13px}
    ul{padding-left:18px} code{color:#7dd3fc} li{margin:4px 0}
    pre{background:#162033;padding:12px;border-radius:8px;overflow:auto;max-height:220px;font-size:12px}
  </style></head><body>
    <h1>Source review</h1>
    <p>This project is code (Python/TS/native) rather than a clickable HTML app. Studio still lists files so you can check the client, then ask chat for a <code>preview/index.html</code> sidecar if you want a runnable demo.</p>
    <p class="muted">Tips</p><ul>${tips}</ul>
    <p class="muted">Files</p><ul>${files || '<li>None yet</li>'}</ul>
    ${readme ? `<p class="muted">README excerpt</p><pre>${readme.slice(0, 4000).replace(/</g, '&lt;')}</pre>` : ''}
  </body></html>`;
}

export function assemblePreviewHtml(
  project?: ProjectState | null,
  opts?: AssemblePreviewOpts,
): string {
  const files = project?.files || {};
  const htmlFile = previewHtmlFile(project);

  if (!htmlFile?.content) {
    if (Object.keys(files).length > 0 && project) return injectBoot(sourceReviewHtml(project), opts);
    return `<!DOCTYPE html><html><body style="font-family:system-ui;padding:48px 32px;color:#64748b;text-align:center">
      <p style="font-size:15px;margin:0 0 8px">Blank canvas</p>
      <p style="margin:0;line-height:1.5">This is a new app. Describe it in chat, attach a chain in Settings, or turn on NutritionAI.</p>
    </body></html>`;
  }

  let html = htmlFile.content;
  if (htmlIsExternalIframeShell(html)) {
    html = `<!DOCTYPE html><html><body style="font-family:system-ui;padding:48px 32px;color:#334155;max-width:420px;margin:0 auto">
      <p style="font-size:15px;font-weight:700;margin:0 0 8px">This preview is an external iframe</p>
      <p style="margin:0;line-height:1.5;color:#64748b">Studio cannot run a nested https:// page inside Preview. Ask chat to put the real UI in index.html (header, pages, forms) — chatbot embed snippets belong in README only.</p>
    </body></html>`;
  }

  html = html.replace(/<link([^>]*?)href=["']([^"']+)["']([^>]*)>/gi, (all, pre, href, post) => {
    const rec = fileByHref(files, href);
    if (rec?.content && ((href as string).toLowerCase().endsWith('.css') || rec.language === 'css')) {
      return `<style>\n${rec.content}\n</style>`;
    }
    if (rec?.url) return `<link${pre}href="${rec.url}"${post}>`;
    return all;
  });

  html = html.replace(
    /<script([^>]*?)src=["']([^"']+)["']([^>]*)>\s*<\/script>/gi,
    (all, pre, src, post) => {
      const rec = fileByHref(files, src);
      if (rec?.content) return `<script${pre}${post}>\n${rec.content}\n</script>`;
      return all;
    },
  );

  html = html.replace(/(src|href)=["']([^"']+)["']/gi, (all, attr, path) => {
    const rec = fileByHref(files, path);
    if (rec?.url) return `${attr}="${rec.url}"`;
    return all;
  });

  return injectBoot(html, opts);
}

export function remoteProjectSignature(project?: ProjectState | null): string {
  const files = project?.files || {};
  return (
    Object.keys(files)
      .sort()
      .map((p) => {
        const f = files[p];
        return `${p}:${(f.content || '').length}:${f.url || ''}`;
      })
      .join('|') + `|${project?.preview_path || ''}`
  );
}
